import {
  PublicClientApplication,
  type Configuration,
  type RedirectRequest,
  type SilentRequest,
} from '@azure/msal-browser';

const tenantId = import.meta.env.VITE_ENTRA_TENANT_ID ?? 'common';
const clientId = import.meta.env.VITE_ENTRA_CLIENT_ID ?? '00000000-0000-0000-0000-000000000000';
const redirectUri = import.meta.env.VITE_ENTRA_REDIRECT_URI ?? window.location.origin;

/**
 * Audience for the access token sent to the Function App / APIM. Architecture
 * §4.4.3 specifies `api://qargo-ims`, but the Phase-1 scaffold defaulted to
 * `api://qargo.tmkngroup.com`. We read it from VITE_ENTRA_API_AUDIENCE so the
 * audience can be flipped once the app registration is finalized without a
 * code change.
 */
const apiAudience = import.meta.env.VITE_ENTRA_API_AUDIENCE ?? 'api://qargo.tmkngroup.com';
const apiScope = `${apiAudience}/access_as_user`;

const msalConfig: Configuration = {
  auth: {
    clientId,
    authority: `https://login.microsoftonline.com/${tenantId}`,
    redirectUri,
    navigateToLoginRequestUrl: true,
  },
  cache: {
    cacheLocation: 'sessionStorage',
    storeAuthStateInCookie: false,
  },
};

export const msalInstance = new PublicClientApplication(msalConfig);

// Initialize() must complete before MsalProvider mounts. main.tsx awaits this.
export const msalReady: Promise<void> = msalInstance.initialize().then(() => {
  return msalInstance
    .handleRedirectPromise()
    .then((result) => {
      if (result?.account) {
        msalInstance.setActiveAccount(result.account);
      } else {
        const existing = msalInstance.getAllAccounts()[0];
        if (existing && !msalInstance.getActiveAccount()) {
          msalInstance.setActiveAccount(existing);
        }
      }
    })
    .catch(() => {
      // handleRedirectPromise errors are surfaced through MSAL event callbacks; swallow here.
    });
});

/** Used at login time — requests OIDC + the API access_as_user scope. */
export const loginRequest: RedirectRequest = {
  scopes: ['openid', 'profile', 'email', apiScope],
};

/** Used by apiFetch to acquire an access token for the API audience. */
export const apiTokenRequest: SilentRequest = {
  scopes: [apiScope],
};
